import { SwapOutlined } from '@ant-design/icons';
import type { GetRef } from 'antd/es/_util/type';
import { default as Select } from 'antd/es/select';
import { useCallback, useRef, useState } from 'react';

import { filterOption } from 'utils/antd/filters-option';
import { hasValue } from 'utils/hasValue';

import { TertiaryButton } from '@atoms/buttons/TertiaryButton';
import { Spinner } from '@atoms/Spinner/Spinner';

import { AmountInput } from '@molecules/AmountInput';
import { LabelledInput } from '@molecules/LabelledInput';

import {
  FX_AMOUNT_TEST_ID,
  FX_RESET_BUTTON,
  FX_TEST_ID_BUY_CURRENCY,
  FX_TEST_ID_PAY_CURRENCY,
  TRANSLATIONS
} from './constants';
import { ExchangeRate } from './ExchangeRate';
import { useCurrencyOptions } from './useCurrencyOptions'; // Import the new hook

export const FXWidget = () => {
  const [currentPayCurrency, setCurrencyPayCurrency] = useState<
    string | undefined
  >(undefined);
  const [currentBuyCurrency, setCurrentBuyCurrency] = useState<
    string | undefined
  >(undefined);
  const [amountToConvert, setAmountToConvert] = useState(1);

  const payCurrencyRef = useRef<GetRef<typeof Select>>(null);
  const buyCurrencyRef = useRef<GetRef<typeof Select>>(null);

  const { pays, buys, isLoading } = useCurrencyOptions(
    currentPayCurrency,
    currentBuyCurrency
  );

  const reset = useCallback(() => {
    setCurrentBuyCurrency(undefined);
    setCurrencyPayCurrency(undefined);
  }, []);

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <section
      aria-label="Exchange Rates"
      className="cool-shadow flex w-full flex-col gap-2 rounded-2xl border bg-white p-4"
    >
      <h3 className="text-md font-bold">{TRANSLATIONS.WIDGET_TITLE}</h3>
      <div className="flex flex-row flex-wrap justify-between gap-2">
        <div className="flex w-full flex-col gap-2">
          <div className="flex w-full flex-row items-center gap-2">
            <div className="w-[200px]">
              <LabelledInput label="Amount">
                <AmountInput
                  placeholder="1"
                  data-testid={FX_AMOUNT_TEST_ID}
                  onChange={(amount) => {
                    if (hasValue(amount) && amount.length > 0) {
                      setAmountToConvert(parseFloat(amount));
                    }
                  }}
                  currency={currentPayCurrency}
                />
              </LabelledInput>
            </div>
            <LabelledInput label="From">
              <Select
                filterOption={filterOption}
                className="w-[200px]"
                showSearch
                value={currentPayCurrency}
                placeholder="Pay currency"
                data-testid={FX_TEST_ID_PAY_CURRENCY}
                options={pays}
                onSelect={(selectedCurrency: string) => {
                  if (selectedCurrency) {
                    setCurrencyPayCurrency(selectedCurrency);
                    payCurrencyRef?.current?.blur?.();
                  }
                }}
                ref={payCurrencyRef}
              />
            </LabelledInput>
            <div className="pt-4">
              <SwapOutlined
                onClick={() => {
                  setCurrentBuyCurrency(currentPayCurrency);
                  setCurrencyPayCurrency(currentBuyCurrency);
                }}
              />
            </div>
            <LabelledInput label="To">
              <Select
                className="w-[200px]"
                showSearch
                value={currentBuyCurrency}
                filterOption={filterOption}
                placeholder="Buy currency"
                data-testid={FX_TEST_ID_BUY_CURRENCY}
                options={buys}
                onSelect={(selectedCurrency: string) => {
                  if (selectedCurrency) {
                    setCurrentBuyCurrency(selectedCurrency);
                    buyCurrencyRef?.current?.blur?.();
                  }
                }}
                ref={buyCurrencyRef}
              />
            </LabelledInput>
            <div className="pt-4">
              <TertiaryButton data-testid={FX_RESET_BUTTON} onClick={reset}>
                Reset
              </TertiaryButton>
            </div>
          </div>

          {hasValue(currentPayCurrency) && hasValue(currentBuyCurrency) ? (
            <ExchangeRate
              amountToConvert={amountToConvert}
              payCurrency={currentPayCurrency}
              buyCurrency={currentBuyCurrency}
            />
          ) : null}
        </div>
      </div>
    </section>
  );
};
